import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetchPublicChallenges, type PublicChallengeItem } from "@/features/public/api";
import { hasMemberAccessToken, restoreMemberAccessToken } from "@/features/public/memberSession";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

function formatDaysLeft(deadline: string) {
  const daysLeft = Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  if (daysLeft <= 0) {
    return "Terminé";
  }

  return `${daysLeft}j restants`;
}

function getChallengesErrorMessage(error: unknown) {
  if (error instanceof Error && error.message.includes("Challenge.status")) {
    return "La base locale n'a pas encore reçu la migration des défis. Lancez la migration Prisma puis redémarrez le backend.";
  }

  return error instanceof Error ? error.message : "Impossible de charger les défis pour le moment.";
}

function ChallengeCard({ challenge, onOpen }: { challenge: PublicChallengeItem; onOpen: () => void }) {
  return (
    <div
      className="rounded-2xl border border-gray-100 bg-white p-5 cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${challenge.bgColor}`}>
          <i className={`${challenge.icon} ${challenge.color} text-xl`}></i>
        </div>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
          {formatDaysLeft(challenge.deadline)}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-700">
          {challenge.sport}
        </span>
        {challenge.createdBySquadLeader ? (
          <span className="rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">
            Squad
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-600">
            Individuel
          </span>
        )}
      </div>

      <h3 className="font-bold text-gray-900">{challenge.title}</h3>
      <p className="mt-2 text-sm text-gray-600 line-clamp-3">{challenge.description}</p>

      {challenge.image ? (
        <img
          src={challenge.image}
          alt={challenge.title}
          className="mt-4 h-36 w-full rounded-2xl object-cover"
        />
      ) : null}

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-gray-400">Participants</p>
          <p className="mt-1 font-semibold text-gray-900">{challenge.participants}</p>
        </div>
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-gray-400">Objectif</p>
          <p className="mt-1 font-semibold text-gray-900">
            {challenge.target.toLocaleString("fr-FR")} {challenge.unit}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-gray-400">Créateur</p>
          <p className="truncate text-sm font-medium text-gray-700">{challenge.creatorName ?? "Communauté ASC"}</p>
        </div>
        <div className="flex -space-x-2">
          {challenge.leaderboard.slice(0, 3).map((participant, index) =>
            participant.avatar ? (
              <img
                key={`${challenge.id}-${participant.rank}-${index}`}
                src={participant.avatar}
                alt={participant.name}
                className="h-8 w-8 rounded-full border-2 border-white object-cover object-[center_24%]"
              />
            ) : (
              <div
                key={`${challenge.id}-${participant.rank}-${index}`}
                className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-[10px] font-semibold text-gray-600"
              >
                {participant.name.charAt(0).toUpperCase()}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function ChallengeModal({ challenge, onClose }: { challenge: PublicChallengeItem; onClose: () => void }) {
  const navigate = useNavigate();
  const isLoggedIn = hasMemberAccessToken();

  const handleChallengeAccess = () => {
    navigate(isLoggedIn ? "/membre?tab=challenges" : "/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dchallenges");
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
        <div className={`${challenge.bgColor} p-6 rounded-t-2xl`}>
          <div className="flex items-start justify-between gap-3">
            <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-white ${challenge.color}`}>
              <i className={`${challenge.icon} text-2xl`}></i>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/50 text-gray-600 hover:bg-white cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-gray-600">
              {challenge.sport}
            </span>
            <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-gray-600">
              {challenge.createdBySquadLeader ? "Défi de squad" : "Défi individuel"}
            </span>
          </div>

          <h2 className="mt-4 text-xl font-bold text-gray-900">{challenge.title}</h2>
          <p className="text-sm text-gray-600 mt-1">{challenge.description}</p>
          {challenge.image ? (
            <img src={challenge.image} alt={challenge.title} className="mt-4 h-48 w-full rounded-2xl object-cover" />
          ) : null}
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Objectif</p>
              <p className="mt-1 font-semibold text-gray-900">
                {challenge.target.toLocaleString("fr-FR")} {challenge.unit}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Participants</p>
              <p className="mt-1 font-semibold text-gray-900">{challenge.participants}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Date limite</p>
              <p className="mt-1 font-semibold text-gray-900">{formatDaysLeft(challenge.deadline)}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Récompense</p>
              <p className="mt-1 font-semibold text-gray-900">{challenge.reward}</p>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-3">Classement</h3>
            <div className="space-y-2">
              {challenge.leaderboard.length > 0 ? (
                challenge.leaderboard.map((participant) => (
                  <div key={`${challenge.id}-${participant.rank}`} className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3">
                    <span className="w-8 text-sm font-bold text-gray-400">
                      {participant.badge || `#${participant.rank}`}
                    </span>
                    {participant.avatar ? (
                      <img src={participant.avatar} alt={participant.name} className="h-9 w-9 rounded-full object-cover object-[center_24%]" />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
                        {participant.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="flex-1 text-sm font-medium text-gray-900">{participant.name}</span>
                    <span className="text-sm font-semibold text-gray-700">
                      {participant.score.toLocaleString("fr-FR")} {challenge.unit}
                    </span>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-center text-sm text-gray-400">
                  Aucun participant classé pour le moment.
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleChallengeAccess}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-[#E8521A] text-white hover:bg-[#d44a16]"
          >
            {isLoggedIn ? (
              <><i className="ri-shield-user-line mr-2"></i>Gérer depuis mon espace membre</>
            ) : (
              <><i className="ri-login-box-line mr-2"></i>Se connecter pour participer</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChallengesBoard() {
  const [challenges, setChallenges] = useState<PublicChallengeItem[]>([]);
  const [selectedChallenge, setSelectedChallenge] = useState<PublicChallengeItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChallenges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = await restoreMemberAccessToken();
      const payload = await fetchPublicChallenges({
        accessToken: accessToken ?? undefined
      });

      setChallenges(payload);
    } catch (caughtError) {
      setError(getChallengesErrorMessage(caughtError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadChallenges();
  }, [loadChallenges]);

  useRealtimeInvalidation({
    topics: ["challenges"],
    onInvalidate: () => {
      void loadChallenges();
    }
  });

  const squadChallenges = useMemo(
    () => challenges.filter((challenge) => challenge.createdBySquadLeader),
    [challenges]
  );
  const individualChallenges = useMemo(
    () => challenges.filter((challenge) => !challenge.createdBySquadLeader),
    [challenges]
  );

  return (
    <div className="space-y-10">
      <div>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#E8521A]/10">
            <i className="ri-team-line text-[#E8521A]"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Défis de squad</h3>
            <p className="text-xs text-gray-500">Défis créés par les membres qui portent déjà un squad dans l&apos;ASC.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {loading ? (
            <>
              <div className="h-56 rounded-2xl bg-gray-100 animate-pulse"></div>
              <div className="h-56 rounded-2xl bg-gray-100 animate-pulse"></div>
            </>
          ) : error ? (
            <div className="sm:col-span-2 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">
              {error}
            </div>
          ) : squadChallenges.length ? (
            squadChallenges.map((challenge) => (
              <ChallengeCard key={challenge.id} challenge={challenge} onOpen={() => setSelectedChallenge(challenge)} />
            ))
          ) : (
            <div className="sm:col-span-2 rounded-2xl border border-dashed border-gray-200 bg-white px-5 py-6 text-sm text-gray-600">
              Aucun défi de squad n&apos;est disponible pour le moment.
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50">
            <i className="ri-user-star-line text-amber-600"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Défis individuels</h3>
            <p className="text-xs text-gray-500">Défis personnels visibles en lecture pour toute la communauté.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {loading ? (
            <>
              <div className="h-56 rounded-2xl bg-gray-100 animate-pulse"></div>
              <div className="h-56 rounded-2xl bg-gray-100 animate-pulse"></div>
            </>
          ) : error ? null : individualChallenges.length ? (
            individualChallenges.map((challenge) => (
              <ChallengeCard key={challenge.id} challenge={challenge} onOpen={() => setSelectedChallenge(challenge)} />
            ))
          ) : (
            <div className="sm:col-span-2 rounded-2xl border border-dashed border-gray-200 bg-white px-5 py-6 text-sm text-gray-600">
              Aucun défi individuel n&apos;est disponible pour le moment.
            </div>
          )}
        </div>
      </div>

      {selectedChallenge ? (
        <ChallengeModal challenge={selectedChallenge} onClose={() => setSelectedChallenge(null)} />
      ) : null}
    </div>
  );
}
